import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  FlexboxGrid,
  Footer,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { CustomerServices } from "../../../core/services/CustomerServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { setSearchString } from "../../../redux/user";
import AddCustomerModal from "../Customers/AddCustomerModal";
import {
  CustomerFields,
  CustomerListParams,
  Permission,
} from "./CustomerDataTypes";
import "./Customers.css";
import { isEmptyVal } from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import AddressCell from "../../components/Elements/AddressCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import { displayField } from "../../../utils/helpers";
import {
  roleTypeArr,
  statusArr,
  messageTypeArr,
  appConstObj,
  custSourceArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import RadioCell from "../../components/Elements/RadioCell";
import PhoneCell from "../../components/Elements/PhoneCell";
import LastOrderCell from "../../components/Elements/LastOrderCell";
import CustomerFinderSingleAction from "./CustomerFinderSingleAction";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function CustomerFinder({
  customerData,
  setCustomerData,
  setProductForGender,
  customerId,
  setStopSubmit,
  ...props
}: {
  customerData?: any;
  setCustomerData?: Function;
  setProductForGender?: Function;
  customerId?: any;
  setStopSubmit?: Function;
}) {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CustomerFields[]>([]);
  const headerCompRef = useRef({});
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const company = useSelector((state: any) => state.company);
  const modalRef = useRef<ImageModal>(null);
  const [selectedCustomer, setSelectedCustomerData] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<any>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const [openModalByName, setOpenModalByName] = useState("");
  const [checkedKeys, setCheckedKeys] = useState<number[]>([
    customerId ? parseInt(customerId) : 0,
  ]);
  const dispatch = useDispatch();
  const updatePermission = UtilityServices.hasPermission("update-customer");
  const permissionObj: Permission = {
    update: updatePermission,
  };
  const initSearchFormData = {
    fname: "",
    lname: "",
    email: "",
    username: "",
    store: "",
    profession: "",
    designation: "",
    receive_notification: "",
    mobile: "",
    building: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    address_mobile: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] =
    useState<CustomerListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const navigate = useNavigate();
  const [failedImages, setFailedImages] = useState<any>([]);

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
    const custData =
      keys.length > 0 ? data.find((item) => item.id == value) : undefined;

    setCustomerData?.(custData);

    if (custData?.gender) {
      setProductForGender?.(custData.gender);
    }
  };

  useEffect(() => {
    if (company.id) {
      headerCompRef.current = company.id;
    } else {
      headerCompRef.current = "";
    }
    setPage(1);
    getCustomers(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }, [company]);

  async function getCustomers(params: CustomerListParams) {
    try {
      setLoading(true);
      if (setStopSubmit) setStopSubmit(true);
      const res = await CustomerServices.getCustomers(params);
      const displayCreate =
        get("data.total", res) == 0 &&
        UtilityServices.hasPermission("create-customer") &&
        getCompanyId();
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setShowCreate(displayCreate);
      setLoading(false);
      if (setStopSubmit) setStopSubmit(false);
    } catch (error) {
      setLoading(false);
      if (setStopSubmit) setStopSubmit(false);
    }
  }

  async function init() {
    try {
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  function processCreateCustomer() {
    if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend &&
      !company.id
    ) {
      UtilityServices.showToaster({
        message: t("REQUIRED_SELECT", {
          field: t("COMPANY").toLowerCase(),
        }),
        type: messageTypeArr.error,
      });
      return;
    }
    dispatch(setSearchString(smartSearchInput));
    setOpenModalByName("add-customer");
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  function onAddCustomer(custData: any) {
    setSmartSearch("");
    getCustomers(
      getSearchParams({
        page: 1,
        customer: custData?.id,
        smart_search: "",
      })
    );

    setCheckedKeys(custData?.id ? [custData?.id] : []);
    if (setCustomerData !== undefined) {
      setCustomerData(custData);
      if (custData?.user?.gender) {
        setProductForGender?.(custData.user.gender);
      }
    }
    setOpenModalByName("");
  }

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function customerPicModal(value: any) {
    if (
      !failedImages.includes(value.large_image_url) &&
      value.large_image_url != null &&
      value.large_image_url !== ""
    ) {
      setSelectedCustomerData(value);
      modalRef.current?.openModal();
      modalRef.current?.hideDelete();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...{ company: headerCompRef.current },
      ...{ customer: customerId },
      ...data,
    };
    let params: CustomerListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }

    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getCustomers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getCustomers(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  const filterCustomers = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getCustomers(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
      e.preventDefault();
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCustomers(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getCustomers(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  return (
    <div>
      <div className="row">
        <div
          className={showCreate ? "col-md-4 me-3 smrt-bxs" : "col-md-4 me-3"}
        >
          <InputGroup>
            <Input
              className="form-control"
              placeholder={t("SMART_SEARCH_BY", {
                entity:
                  t("NAME").toLowerCase() +
                  ", " +
                  t("PHONE").toLowerCase() +
                  ", " +
                  t("OR").toLowerCase() +
                  " " +
                  t("USERNAME").toLowerCase(),
              })}
              value={smartSearchInput}
              onChange={(val: any) => setSmartSearch(val)}
              onKeyDown={(e: any) => filterCustomers(e)}
            />
            <InputGroup.Button
              title={t("SEARCH")}
              onClick={(e: any) => filterCustomers(e)}
            >
              <SearchIcon />
            </InputGroup.Button>
            <InputGroup.Button
              title={t("CLEAR")}
              onClick={() => clearSmartSearch()}
            >
              <CloseIcon />
            </InputGroup.Button>{" "}
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button appearance="primary" onClick={processCreateCustomer}>
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </InputGroup>
        </div>
      </div>

      <div className={total > 0 ? "cust-finder-table" : "no-record-table"}>
        <Table
          height={50}
          fillHeight={true}
          data={data}
          loading={loading}
          onSortColumn={onSortChange}
          sortColumn={sort_by}
          bordered={true}
          cellBordered
          sortType={sort_order}
          hover={true}
        >
          <Column width={30} align="center" fixed>
            <CustomHeaderCell>
              <div style={{ lineHeight: "40px" }}></div>
            </CustomHeaderCell>
            <CheckCell
              dataKey="id"
              checkedKeys={checkedKeys}
              onChange={handleCheck}
            />
          </Column>
          <Column width={50} align="center">
            <CustomHeaderCell>{t("IMAGE")}</CustomHeaderCell>
            <ImageCellFormatter
              dataKey="large_image_url"
              rowData={(rowData: any) => rowData}
              dummyImage={UtilityServices.getLoggedInUser("user_default_image")}
              onFailedImages={getFailedImages}
              openPictureModal={customerPicModal}
            />
          </Column>
          <Column width={130} sortable resizable>
            <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
            <CustomCell dataKey="name">
              {(rowData: any) => <span>{displayField(rowData.name)}</span>}
            </CustomCell>
          </Column>

          <Column width={100}>
            <CustomHeaderCell>{t("PHONE")}</CustomHeaderCell>
            <PhoneCell
              positionFromRight="60%"
              rowData={(rowData: any) => rowData}
            />
          </Column>

          <Column flexGrow={1}>
            <CustomHeaderCell>{t("ADDRESS")}</CustomHeaderCell>
            <AddressCell rowData={(rowData: any) => rowData} />
          </Column>
          <Column width={120} resizable>
            <CustomHeaderCell>{t("STORE")}</CustomHeaderCell>
            <CustomCell dataKey="store_name">
              {(rowData: any) => (
                <span>{displayField(rowData.store_name)}</span>
              )}
            </CustomCell>
          </Column>
          <Column width={130}>
            <CustomHeaderCell>
              {t("LAST")} {t("ORDER")}
            </CustomHeaderCell>
            <LastOrderCell
              positionFromRight="10%"
              rowData={(rowData: any) => rowData}
            />
          </Column>
          <Column width={50} fixed="right">
            <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
            <Cell>
              {(rowData) => (
                <CustomerFinderSingleAction
                  customerData={rowData}
                  permission={permissionObj}
                />
              )}
            </Cell>
          </Column>
        </Table>
      </div>
      {openModalByName === "add-customer" && (
        <AddCustomerModal
          openModal={openModalByName === "add-customer" ? true : false}
          setOpenModal={setOpenModalByName}
          onAddCustomer={onAddCustomer}
          defaultValues={{
            customer_source_id: custSourceArr.existing_customer,
          }}
        />
      )}
      <Footer className="footer">
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={["total", "-", "limit", "|", "pager", "skip"]}
          total={total}
          limitOptions={appConstObj.limit_options}
          limit={rows}
          activePage={page}
          onChangePage={onPageChange}
          onChangeLimit={handleChangeRows}
        />
      </Footer>
      <ToastContainer />

      <ImageModal
        ref={modalRef}
        modalTitle={t("PROFILE_PIC")}
        heading={t("PROFILE_PIC")}
        body={
          <img
            src={selectedCustomer.large_image_url}
            className="main-profile-pic"
            alt={t("PROFILE_PIC")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
    </div>
  );
}

export default CustomerFinder;
